import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { MonitorPlayIcon, ChartLineIcon } from '@phosphor-icons/react';
import { Chart, ChartDataPoint, ChartSkeleton } from '@/components/Chart';
import { SegmentedControl } from '@/components/SegmentedControl';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { EngineService } from '@/services/engine';
import { AppRoutes } from '@/constants/routes';
import { AlertRuleStats, AlertRuleStatsSourceData } from '@/types';
import { stripFileExtension } from '@/utils/file-name';
import { toUtcTimestamp } from '@/utils/date';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    PERIOD_CONFIG,
    getStartTime,
    formatChartAxisByGroupBy,
    usePeriodOptions,
    useChartTooltipProps,
    type PeriodOption,
} from '@/utils/chart-periods';
import { useChartPointNavigation } from '@/hooks/useChartPointNavigation';

interface AlertChartProps {
    alertRuleId: string;
}

const MAX_SOURCES_DISPLAY = 24;

export function AlertChart({ alertRuleId }: AlertChartProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const navigateToChartRange = useChartPointNavigation();
    const [activePeriod, setActivePeriod] = useState<PeriodOption>('1hour');

    const config = PERIOD_CONFIG[activePeriod];
    const groupBy = config.groupBy;
    const startTime = getStartTime(config.milliseconds, groupBy);

    const { data: stats, isLoading } = useSWR<AlertRuleStats>(
        `alert-stats-${alertRuleId}-${activePeriod}`,
        () =>
            EngineService.getAlertRuleStats(alertRuleId, {
                startTime,
                groupBy,
            }),
        { keepPreviousData: true },
    );

    const chartData = useMemo(() => stats?.chartData || [], [stats?.chartData]);
    const eventsCount = useMemo(
        () => stats?.totalEvents || 0,
        [stats?.totalEvents],
    );
    const displaySources = useMemo(
        () => (stats?.sourceStats || []).slice(0, MAX_SOURCES_DISPLAY),
        [stats?.sourceStats],
    );
    const { tooltipSources, tooltipTitleFormatter } = useChartTooltipProps(
        groupBy,
        displaySources,
    );

    const periodOptions = usePeriodOptions(t);

    const handlePointClick = (_dataPoint: ChartDataPoint, index: number) => {
        const point = stats?.chartData[index];
        if (!point) return;
        navigateToChartRange(
            AppRoutes.ALERTS,
            { alertRuleId },
            point.startTime,
            point.endTime,
        );
    };

    const handleSourceClick = (source: AlertRuleStatsSourceData) => {
        const params = new URLSearchParams();
        params.set('alertRuleId', alertRuleId);
        params.set('sources', source.id);

        const periodStartTime = getStartTime(config.milliseconds, groupBy);
        params.set('startTime', periodStartTime.toString());
        params.set('endTime', toUtcTimestamp(new Date()).toString());

        navigate(`${AppRoutes.ALERTS}?${params.toString()}`);
    };

    const xAxisTickFormatter = useCallback(
        (value: string) => formatChartAxisByGroupBy(value, groupBy),
        [groupBy],
    );

    return (
        <div className="w-full max-w-[900px] mx-auto text-white-100">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex-1">
                    <div className="text-sm text-grey-200 mb-2">
                        {t('chart.events')}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white-100">
                        <MonitorPlayIcon className="w-4 h-4 text-grey-200" />
                        <AnimatedNumber
                            key={activePeriod}
                            value={eventsCount}
                        />
                        <span className="text-sm text-white-100">
                            {t(`chart.inLast.${activePeriod}`)}
                        </span>
                    </div>
                </div>

                <SegmentedControl
                    value={activePeriod}
                    onValueChange={setActivePeriod}
                    options={periodOptions}
                />
            </div>

            <div className="mb-12">
                {!stats && isLoading ? (
                    <ChartSkeleton height={298} />
                ) : chartData.length > 0 ? (
                    <Chart
                        data={chartData}
                        dataKey="value"
                        strokeColor="var(--brand)"
                        fillColor="var(--brand)"
                        height={298}
                        tooltipLabel={t('chart.events')}
                        tooltipSources={tooltipSources}
                        tooltipTitleFormatter={tooltipTitleFormatter}
                        onPointClick={handlePointClick}
                        xAxisTickFormatter={xAxisTickFormatter}
                        xAxisInterval={config.xAxisInterval}
                        margin={{
                            top: 10,
                            right: 20,
                            left: -20,
                            bottom: config.bottomMargin || 0,
                        }}
                    />
                ) : (
                    <div className="h-[298px] flex flex-col items-center justify-center rounded-lg border border-dashed border-grey-500 bg-grey-700/30">
                        <div className="w-16 h-16 rounded-full bg-grey-600/50 flex items-center justify-center mb-4">
                            <ChartLineIcon className="w-8 h-8 text-grey-400" />
                        </div>
                        <div className="text-sm text-grey-300 mb-1">
                            {t('chart.noData')}
                        </div>
                        <div className="text-xs text-grey-400">
                            {t('chart.noDataDescription')}
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 @medium:grid-cols-3 gap-x-8 gap-y-4">
                {displaySources.map((item: AlertRuleStatsSourceData) => (
                    <div key={item.id} className="flex flex-col gap-2">
                        <button
                            onClick={() => handleSourceClick(item)}
                            className="text-xs-medium text-grey-200 hover:text-white-100 transition-colors text-left cursor-pointer"
                        >
                            {stripFileExtension(item.name)}
                        </button>
                        <div className="flex items-center gap-4">
                            <div className="flex-1 h-2.5 bg-grey-800 rounded-sm overflow-hidden">
                                <div
                                    className="h-full bg-grey-600 rounded-sm transition-all"
                                    style={{
                                        width: `${(item.value / eventsCount) * 100}%`,
                                    }}
                                />
                            </div>
                            <div className="text-xs text-white-100 min-w-[15px]">
                                {item.value}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
